import type { Expression } from './expression';
import type { Face } from './faces';
import { blinkAt, Eye } from './HumanFace';

type CreaturePose = {
  /** Ear splay from vertical, degrees. */
  earAngle: number;
  /** 0 upright, 1 pinned flat back. */
  earFlatten: number;
  /** Whole-head tilt, degrees. */
  headTilt: number;
  look: [number, number];
  mouth: number;
};

/**
 * The five poses. Note how little they share with the human numbers: the same
 * five names over different anatomy is the work here, and copying the human
 * values would produce exactly the human-in-a-costume this rig exists to
 * avoid.
 */
const POSES: Record<Expression, CreaturePose> = {
  neutral: {
    earAngle: 6,
    earFlatten: 0,
    headTilt: 0,
    look: [0, 0],
    mouth: 0.15,
  },
  // Ears swung forward and head cocked: a capybara attending to something.
  // With no brows to raise, the ears carry nearly the whole expression here,
  // so they move much further than the human brows do.
  listening: {
    earAngle: 26,
    earFlatten: 0,
    headTilt: -9,
    look: [0, 0.6],
    mouth: 0.06,
  },
  // §8.1 asks for stillness. No eyebrows to drop, so it is ears up, eyes up,
  // and no tilt at all — the absence of movement is the signal.
  thinking: {
    earAngle: 14,
    earFlatten: 0,
    headTilt: 0,
    look: [0.8, -0.9],
    mouth: 0.08,
  },
  speaking: {
    earAngle: 6,
    earFlatten: 0,
    headTilt: 4,
    look: [0, 0.2],
    mouth: 0.95,
  },
  // Concern is ears flattening back and a head tilt, which is how the animal
  // actually shows it. A brow raise has nowhere to happen here.
  concern: {
    earAngle: -14,
    earFlatten: 1,
    headTilt: 13,
    look: [0, 0.45],
    mouth: 0.2,
  },
  // Ears right forward and up, head level, mouth open. A capybara's delight is
  // in the ears, the way its concern is — there is nowhere else for it to go.
  celebrate: {
    earAngle: 32,
    earFlatten: 0,
    headTilt: -2,
    look: [0, 0.1],
    mouth: 0.65,
  },
};

/**
 * The creature rig. Same props and same five expressions as `HumanFace`, so
 * `TutorAvatar` can choose without either rig leaking through the interface —
 * which is the promise Q1 makes and the reason there are two rigs at all.
 *
 * A capybara reads from three things in this order: a head that is wider than
 * tall and flat on top, small round ears set high and wide, and a blunt square
 * muzzle. Get the outline right and the rest is detail — §8.3's argument about
 * identity at small sizes, applied to an animal.
 */
export function CreatureFace({ face, expression, phase }: { face: Face; expression: Expression; phase: number }) {
  // Slower than the humans. A capybara at a human breathing rate looks anxious,
  // which is the one thing this character is not.
  const breathe = Math.sin(phase / 1600);
  const blink = blinkAt(phase);
  const pose = POSES[expression];
  const w = face.headWidth;

  return (
    <svg
      viewBox="0 0 100 100"
      className="h-full w-full"
      role="presentation"
      style={{
        transform:
          `translateY(calc(${breathe.toFixed(3)} * var(--tutor-intensity) * 1.2px)) ` +
          `rotate(calc(${pose.headTilt}deg * var(--tutor-intensity)))`,
        transformOrigin: '50px 78px',
      }}
    >
      <Ear cx={50 - w / 2 + 6} angle={pose.earAngle} flatten={pose.earFlatten} color={face.hairColor} />
      <Ear cx={50 + w / 2 - 6} angle={-pose.earAngle} flatten={pose.earFlatten} color={face.hairColor} />
      {/* Wider than tall and flat on top. This outline is what reads as
          "capybara" before any detail does. */}
      <rect x={50 - w / 2} y="38" width={w} height="44" rx="18" fill={face.skin} />
      {/* High and far back, near the top corners: that placement is as much
          of the animal as the muzzle is. Mid-face reads as a bear. */}
      <Eye cx={50 - w / 3} cy={49} aspect={face.eye} open={blink} look={pose.look} />
      <Eye cx={50 + w / 3} cy={49} aspect={face.eye} open={blink} look={pose.look} />
      <Muzzle width={w} mouth={pose.mouth} phase={phase} speaking={expression === 'speaking'} />
    </svg>
  );
}

function Ear({ cx, angle, flatten, color }: { cx: number; angle: number; flatten: number; color: string }) {
  return (
    <ellipse
      cx={cx}
      cy="34"
      rx="7"
      ry={7 - flatten * 3}
      fill={color}
      style={{
        transform:
          `rotate(calc(${angle}deg * var(--tutor-intensity))) ` +
          `translateY(calc(${flatten} * var(--tutor-intensity) * 4px))`,
        transformOrigin: `${cx}px 40px`,
      }}
    />
  );
}

function Muzzle({ width, mouth, phase, speaking }: { width: number; mouth: number; phase: number; speaking: boolean }) {
  const talk = speaking ? 0.5 + 0.5 * Math.abs(Math.sin(phase / 90)) : 1;
  const h = mouth * talk * 11;
  return (
    <g>
      {/* Blunt and square, sitting proud of the face. The second thing that
          reads as capybara after the head outline. */}
      <rect x={50 - width / 4} y="62" width={width / 2} height="20" rx="9" fill="#C99A6B" />
      <ellipse cx={45} cy="68" rx="2.2" ry="1.6" fill="#5A3A28" />
      <ellipse cx={55} cy="68" rx="2.2" ry="1.6" fill="#5A3A28" />
      <path d={`M42 76 q8 ${h + 2} 16 0`} fill="none" stroke="#5A3A28" strokeWidth="2" strokeLinecap="round" />
    </g>
  );
}
